<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import * as yup from "yup"
import { useField, useForm } from "vee-validate"
import { countMondays, formatDate, parseDate } from "@core/helpers"
import { useVModel } from "@vueuse/core"
import { useResponsive } from "@core/composables"
import moment from "moment"

// Type représentant une option de raccourci
type ShortcutOption = {
    label: string
    value: string
    disabled?: boolean
}

type Props = {
    /**
     * Identifiant de l'input
     */
    inputId?: string
    /**
     * Période sélectionné entre 2 dates
     */
    period: [Date | null, Date | null]
    /**
     * Le type de choix fait depuis les raccourcis
     * (current_week, last_7days, last_week, last_30days, last_month, last_6months, last_12months, custom)
     */
    choice: string
    /**
     * La vue du calendrier (date, month, year)
     */
    view?: string
    /**
     * Liste des raccourcis
     */
    shortcuts?: ShortcutOption[]
    /**
     * Liste des jours désactivés (index du jour dans la semaine (0 = dimanche, 1 = lundi, ..., 6 = samedi))
     */
    disabledDays?: number[]
    /**
     * Indique si le composant est en erreur. Bloque le bouton de validation
     */
    error?: boolean
    /**
     * Indique si le composant est désactivé
     */
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    inputId: "calendar-period",
    view: "date",
    shortcuts: () => [],
    disabledDays: () => [],
    error: false,
    disabled: false,
})

const emit = defineEmits(["update:period", "update:choice", "click:shortcut"])

const { isMobile } = useResponsive()

//======================================================
//                   Lang
//======================================================

const { t } = useI18n({
    messages: {
        fr: {
            labels: {
                delete: "Réinitialiser",
                apply: "Appliquer",
                quit: "Fermer",
            },
            error_message: "",
            start_date: "Date de début",
            end_date: "Date de fin",
            week_info: {
                title: "Sélection des semaines",
                content:
                    'Pour visualiser une semaine, veuillez sélectionner une période du lundi au dimanche. Sans cela, les semaines non complétes ne seront pas affichées. Le cercle <span class="date-warn-dot date-warn-dot-tooltip"></span> indique les jours non sélectionnés pour faire une semaine complète.',
            },
            diff_days: {
                date: "0 jour sélectioné | 1 jour sélectioné | {count} jours sélectionnés",
                week: "0 semaine sélectionnée | 1 semaine sélectionnée | {count} semaines sélectionnées",
                month: "0 mois sélectionné | 1 mois sélectionné | {count} mois sélectionnés",
                year: "0 année sélectionnée | 1 année sélectionnée | {count} années sélectionnées",
            },
        },
    },
})

//======================================================
// Données et V-model
//======================================================

/**
 * Modèle de la période
 */
const modelPeriod = useVModel(props, "period", emit, {
    eventName: "update:period",
    defaultValue: [new Date(), new Date()],
    deep: true,
    passive: true,
})
/**
 * Modèle du choix de raccourci
 */
const modelChoice = useVModel(props, "choice", emit, {
    eventName: "update:choice",
    defaultValue: "last_7days",
    deep: true,
    passive: true,
})

/**
 * Ancienne valeur du choix de raccourci
 */
const previousChoice = ref("")
/**
 * Ancienne valeur de la période
 */
const previousPeriod = ref<[Date | null, Date | null]>([null, null])

//======================================================
// Gestion du formulaire
// - Formulaire de validation des dates de périodes
// - Gestion du bouton de validation
//======================================================
const schema = yup.object({
    startDate: yup
        .string()
        .nullable()
        .matches(
            /^\d{2}\/\d{2}\/\d{4}$/,
            "La date de début doit être au format dd/mm/yyyy."
        )
        .test(
            "value-valid",
            "La date de début est invalide (jours, mois ou année incorrects).",
            (value) => !value || isValidDateValue(value)
        )
        .test(
            "start-required-if-end",
            "Une date de début est requise car une date de fin est définie.",
            function (value) {
                const { endDate } = this.parent
                return !endDate || value != null
            }
        )
        .test(
            "start-before-end",
            "La date de début doit être antérieure à la date de fin.",
            function (value) {
                const { endDate } = this.parent
                if (!value || !endDate) return true

                const startDateObj = parseDate(value)
                const endDateObj = parseDate(endDate)

                if (!startDateObj || !endDateObj) return false
                return startDateObj.getTime() <= endDateObj.getTime()
            }
        ),

    endDate: yup
        .string()
        .nullable()
        .matches(
            /^\d{2}\/\d{2}\/\d{4}$/,
            "La date de fin doit être au format dd/mm/yyyy."
        )
        .test(
            "value-valid",
            "La date de fin est invalide (jours, mois ou année incorrects).",
            (value) => !value || isValidDateValue(value)
        )
        .test(
            "end-required-if-start",
            "Une date de fin est requise car une date de début est définie.",
            function (value) {
                const { startDate } = this.parent
                return !startDate || value != null
            }
        )
        .test(
            "end-after-start",
            "La date de fin doit être postérieure à la date de début.",
            function (value) {
                const { startDate } = this.parent
                if (!value || !startDate) return true

                const startDateObj = parseDate(startDate)
                const endDateObj = parseDate(value)

                if (!startDateObj || !endDateObj) return false
                return endDateObj.getTime() >= startDateObj.getTime()
            }
        )
        .test(
            "end-not-after-max",
            "La date de fin ne peut pas dépasser la date maximale possible.",
            function (value) {
                if (!value) return true
                const endDateObj = parseDate(value)

                if (!endDateObj) return false

                return !moment(endDateObj).isAfter(
                    moment(getMaxDateByView.value)
                )
            }
        ),
})

const { errors, validateField, validate, meta } = useForm({
    validationSchema: schema,
})

const { value: startDate } = useField<string>("startDate")
const { value: endDate } = useField<string>("endDate")

/**
 * Vérifie que la validiée de la date et de son format
 */
function isValidDateValue(dateString: string): boolean {
    const [day, month, year] = dateString.split("/").map(Number)

    if (year < 2000) return false

    if (month < 1 || month > 12) return false

    const daysInMonth = new Date(year, month, 0).getDate()
    return day >= 1 && day <= daysInMonth
}

/**
 * Récupère les configurations des inputs "startDate" et "endDate"
 */
const getInputsConfig = computed(() => {
    return [
        {
            inputId: "startDate",
            label: t("start_date"),
            error: errors.value.startDate,
            model: startDate,
            invalid: !!errors.value.startDate,
        },
        {
            inputId: "endDate",
            label: t("end_date"),
            error: errors.value.endDate,
            model: endDate,
            invalid: !!errors.value.endDate,
        },
    ]
})

//======================================================
// Gestion des boutons du formulaire
//======================================================
/**
 * Quand l'utilisateur clique sur le bouton "appliquer"
 */
async function onClickSubmitCalendarForm() {
    if (
        startDate.value !== formatDate(modelPeriod.value[0]) ||
        endDate.value !== formatDate(modelPeriod.value[1])
    ) {
        startDate.value = formatDate(modelPeriod.value[0])
        endDate.value = formatDate(modelPeriod.value[1])
    }
    const isStartDateValid = await validateField("startDate")
    const isEndDateValid = await validateField("endDate")
    const { valid } = await validate()

    // Si des erreurs sur le formulaire, on ne fait rien
    if (
        !isStartDateValid.valid ||
        !isEndDateValid.valid ||
        !endDate.value ||
        !startDate.value ||
        !valid ||
        props.error
    ) {
        return
    }

    modelPeriod.value = [parseDate(startDate.value), parseDate(endDate.value)]

    visible.value = false
}
/**
 * Quand l'utilisateur clique sur le bouton "réinitialiser"
 * Réinitialisation des inputs
 */
function onReset() {
    emit("click:shortcut", "custom")
}

//======================================================
// Gestion des watchers pour mettre à jour
// les variables dépendantes entre elles
//======================================================
watch(
    startDate,
    (newStartDate) => {
        // si entrain d'être modifié alors change le raccourcie sur "personnalisée"
        if (newStartDate?.includes("_")) {
            modelChoice.value = "custom"
        }

        if (newStartDate != "" && typeof newStartDate === "string") {
            modelPeriod.value = [parseDate(newStartDate), modelPeriod.value[1]]
        }
    },
    { deep: true, immediate: true }
)
watch(
    endDate,
    (newendDate) => {
        if (newendDate?.includes("_")) {
            modelChoice.value = "custom"
        }
        if (newendDate != "" && typeof newendDate === "string") {
            modelPeriod.value = [modelPeriod.value[0], parseDate(newendDate)]
        }
    },
    { deep: true, immediate: true }
)

watch(
    modelChoice,
    () => {
        startDate.value = formatDate(modelPeriod.value[0])
        endDate.value = formatDate(modelPeriod.value[1])
        // valide les champs
        validate()
    },
    { deep: true, immediate: true }
)

//======================================================
// Gestion des raccourcis
//======================================================
/**
 * Récupère la valeur du raccourci sélectionné
 */
const getShortcutSelected = computed(() => {
    return (
        props.shortcuts?.find(
            (shortcut) => shortcut.value === modelChoice.value
        )?.label ?? ""
    )
})

//======================================================
// Gestion du calendrier
//======================================================

/**
 * Récupération de la date maximum possible en fonction de la vue du calendrier
 */
const getMaxDateByView = computed(() => {
    let maxDate
    switch (props.view) {
        // si on est en mois, alors la date maximum est le dernier mois complet à la date du 1er jour du mois
        case "month":
            maxDate = moment().subtract(1, "month").endOf("month")
            break
        // si on est en année, alors la date maximum est la dernière année complète à la date du 1er jour de l'année
        case "year":
            maxDate = moment().subtract(1, "year").endOf("year")
            break
        // si on est en semaine, alors la date maximum est la dernière semaine complète à la date du dernier jour de la semaine (dimanche)
        // Note: En anglais le dimanche est le premier jour de la semaine, c'est pourquoi on utilise startOf("week")
        case "week":
            maxDate = moment().startOf("week")
            break
        default:
            maxDate = moment().subtract(1, "day")
            break
    }
    maxDate = maxDate.toDate()
    maxDate.setHours(0, 0, 0, 0)

    return maxDate
})

/**
 * Récupère le bon format de vue pour le calendrier pour le BaseDatePicker
 * en fonction de la props view
 */
const getViewDatePicker = computed(() => {
    if (props.view === "week") {
        return "date"
    }
    return props.view
})

/**
 * Quand une date est sélectionnée dans le calendrier
 * on passe en mode "personnalisé"
 */
function onDateSelect() {
    modelChoice.value = "custom"
    // met à jour le startDate et endDate
    startDate.value = formatDate(modelPeriod.value[0])
    endDate.value = formatDate(modelPeriod.value[1])
}

/**
 * Récupère les jours non sélectionnés dans les semaines non complète
 * Par exemple si on sélectionne le mercredi d'une semaine, alors
 * on renvoie le lundi, mardi comme dates non sélectionnées.
 *
 * Permet dans la vue "week" de visualiser les semaines incomplètes et aider
 * l'utilisateur à sélectionner une semaine complète
 *
 * @returns Set des jours non sélectionnés
 */
const getDaysNotSelectedInWeeks = computed(() => {
    let missingDays = new Set()
    if (modelPeriod.value[0] && modelPeriod.value[1]) {
        const start = moment(modelPeriod.value[0]).startOf("isoWeek") // Lundi de la semaine de startDate
        const selectedDays = new Set()
        const allDaysInImpactedWeeks = new Set()

        // Stocker tous les jours sélectionnés entre startDate et endDate
        let current = moment(modelPeriod.value[0])
        while (current.isSameOrBefore(modelPeriod.value[1])) {
            selectedDays.add(current.format("YYYY-MM-DD"))
            current.add(1, "day")
        }

        // Stocker tous les jours des semaines concernées
        current = start.clone()
        while (current.isSameOrBefore(moment(modelPeriod.value[0]))) {
            allDaysInImpactedWeeks.add(current.format("YYYY-MM-DD"))
            current.add(1, "day")
        }

        // Trouver les jours manquants
        missingDays = new Set(
            [...allDaysInImpactedWeeks].filter((day) => !selectedDays.has(day))
        )
    }

    return missingDays
})
/**
 * Récupère le nombre de jours/semaines/mois/années entre la date de début et de fin de la période
 */
const getDiffPeriodDuration = computed(() => {
    if (modelPeriod.value[0] && modelPeriod.value[1]) {
        switch (props.view) {
            case "week":
                return countMondays(modelPeriod.value[0], modelPeriod.value[1])
            case "month":
                return (
                    moment(modelPeriod.value[1]).diff(
                        moment(modelPeriod.value[0]),
                        "months"
                    ) + 1
                )
            case "year":
                return (
                    moment(modelPeriod.value[1]).diff(
                        moment(modelPeriod.value[0]),
                        "years"
                    ) + 1
                )
            default:
                return (
                    moment(modelPeriod.value[1]).diff(
                        moment(modelPeriod.value[0]),
                        "days"
                    ) + 1
                )
        }
    }
    return 0
})

/**
 * Fonctiona d'aide indiquant si une date est à l'intérieur d'une semaine non complète
 * @param date - Date à vérifier
 */
function isInsideWeekNotComplete(date: {
    day: number
    month: number
    year: number
}) {
    if (props.view !== "week") return false
    return getDaysNotSelectedInWeeks.value.has(
        moment(date).format("YYYY-MM-DD")
    )
}

//======================================================
// Lifecycle
//======================================================
/**
 * Sélectionne la période la semaine par défaut quand on charge le composant
 */
onBeforeMount(() => {
    emit(
        "click:shortcut",
        props.shortcuts.find((s) => !s.disabled && s.value !== "current_week")
            ?.value
    )
    // mettre à jour la valeur de startDate et endDate
    startDate.value = formatDate(modelPeriod.value[0])
    endDate.value = formatDate(modelPeriod.value[1])
})

//======================================================
// Gestion de la modale
//======================================================
/**
 * Indique si la modale est visible
 */
const visible = ref(false)

/**
 * Quand on clique sur le bouton "fermer" de la modale,
 * on réinitialise les dates si le formulaire n'est pas correct
 */
function onClickQuit() {
    visible.value = false
    // vérifie que le formulaire est correct, on ne fait rien si c'est le cas
    if (meta.value.valid && !props.error) return
    //  sinon si pas correct alors on réinitialise les dates
    modelChoice.value = previousChoice.value
    modelPeriod.value = previousPeriod.value
}

/**
 * Quand la modale s'ouvre, on sauvegarde les valeurs précédentes
 * et on met à jour les dates de début et de fin
 */
function onOpenDialog() {
    previousChoice.value = modelChoice.value
    previousPeriod.value = modelPeriod.value

    // met à jour le startDate et endDate
    startDate.value = formatDate(modelPeriod.value[0])
    endDate.value = formatDate(modelPeriod.value[1])
}
</script>

<template>
    <BaseInputWrapper :input-id="inputId" class="calendar-period">
        <template #header>
            <slot name="header"></slot>
        </template>
        <BaseDialog
            v-model:visible="visible"
            modal
            class="calendar-period-modal"
            @show="onOpenDialog"
            :close-on-escape="false"
            :position="isMobile ? 'center' : 'top'"
        >
            <template #container>
                <!-- Contient la sidebar des raccourcis et le calendrier-->
                <div class="calendar-period-modal__container">
                    <div
                        class="calendar-period-modal__sidebar"
                        v-if="!isMobile"
                    >
                        <BaseButton
                            text
                            v-for="shortcut in shortcuts"
                            :class="[
                                'calendar-period__button',
                                shortcut.value == modelChoice &&
                                    'calendar-period__button--focus',
                                shortcut.disabled &&
                                    'calendar-period__button--disabled',
                                !shortcut.disabled && 'cursor-pointer',
                            ]"
                            :key="shortcut.value"
                            icon="pi pi-check"
                            icon-class="icon"
                            unstyled
                            :disabled="shortcut.disabled"
                            :label="shortcut.label"
                            @click="$emit('click:shortcut', shortcut.value)"
                        ></BaseButton>
                    </div>
                    <div class="calendar-period-modal__content">
                        <slot name="calendar-message"></slot>
                        <div class="calendar-period-modal__form">
                            <!-- Input de start et end dates -->
                            <div
                                v-if="!isMobile"
                                class="calendar-period-modal__datepicker-inputs"
                            >
                                <BaseInputWrapper
                                    class="w-full"
                                    v-for="input in getInputsConfig"
                                    :key="input.inputId"
                                    :error="input.error"
                                    :input-id="input.inputId"
                                    :label="input.label"
                                >
                                    <BaseInputText
                                        :input-id="input.inputId"
                                        v-model="input.model"
                                        v-mask="{
                                            regex: '^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/([1-2][0-0][0-9]{2})$',
                                        }"
                                        :invalid="input.invalid"
                                    ></BaseInputText>
                                </BaseInputWrapper>
                            </div>
                            <!-- Phrase sur la durée de la période -->
                            <div
                                class="calendar-period-modal__diff-days-message"
                            >
                                <p class="text-sm text-primary-700">
                                    {{
                                        t(`diff_days.${view}`, {
                                            count: getDiffPeriodDuration,
                                        })
                                    }}
                                </p>
                            </div>
                            <!-- Calendrier -->
                            <BaseDatePicker
                                class="calendar-period-modal__calendar"
                                v-model="modelPeriod"
                                date-format="dd/mm/yy"
                                inline
                                :view="getViewDatePicker"
                                :number-of-months="isMobile ? 1 : 2"
                                :max-date="getMaxDateByView"
                                :disabled-days="disabledDays"
                                selection-mode="range"
                                @date-select="onDateSelect"
                            >
                                <template #date="slotProps">
                                    <strong
                                        v-if="
                                            isInsideWeekNotComplete(
                                                slotProps.date
                                            )
                                        "
                                        class="date-warn-dot"
                                        >{{ slotProps.date.day }}</strong
                                    >
                                    <template v-else>{{
                                        slotProps.date.day
                                    }}</template>
                                </template>
                            </BaseDatePicker>
                            <div
                                class="calendar-period-modal__diff-days-message-legend"
                                v-if="view === 'week'"
                            >
                                <BaseIcon
                                    class="base-tooltip-message"
                                    icon="pi pi-info-circle"
                                    color="var(--p-primary-500)"
                                    size="font-size: 0.5rem"
                                ></BaseIcon>
                                <p
                                    class="text-xs text-primary-700"
                                    v-html="t('week_info.content')"
                                ></p>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Boutons quitter | réinitialiser | valider -->
                <div class="calendar-period-modal__footer">
                    <BaseButton
                        type="submit"
                        :label="t('labels.quit')"
                        outlined
                        @click="onClickQuit"
                    ></BaseButton>
                    <div class="calendar-period-modal__footer-right">
                        <BaseButton
                            v-if="modelChoice === 'custom'"
                            outlined
                            :label="t('labels.delete')"
                            @click="onReset"
                        ></BaseButton>
                        <BaseButton
                            @click="onClickSubmitCalendarForm"
                            :label="t('labels.apply')"
                        ></BaseButton>
                    </div>
                </div>
            </template>
        </BaseDialog>
        <!-- Sélecteur pour l'affichage du Calendrier -->
        <BaseSelect
            pt:list-container:style="display: none"
            :input-id="inputId"
            @before-show="visible = true"
            :pt="{
                overlay: '!border-none',
            }"
            :invalid="error"
            :disabled="disabled"
        >
            <template #value>
                <p class="text-ellipsis overflow-x-hidden">
                    {{ getShortcutSelected }}
                </p>
            </template>
            <template #footer>
                <!-- Vide car modal ouverte à la place -->
            </template>
        </BaseSelect>
    </BaseInputWrapper>
</template>
<style lang="css">
@import "@styles/breakpoints.css";

/* Modale du calendrier */

.calendar-period-modal {
    .calendar-period-modal__container {
        display: flex;
        flex-direction: row;
        padding: 1rem;
        gap: 1rem;

        .calendar-period-modal__content {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            max-width: 52rem;
        }

        .calendar-period-modal__form {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            padding: 1rem;
            width: 52rem;
            border-radius: 0.375rem;
            background-color: #f6f9fc;
            border: 1px solid #dfe7ef;
            @media (--large-viewport) {
                width: 100%;
                max-width: 30rem;
            }
        }

        .calendar-period-modal__datepicker-inputs {
            display: flex;
            flex-direction: row;
            gap: 0.5rem;
        }

        .calendar-period-modal__diff-days-message {
            text-align: center;
        }
        .calendar-period-modal__diff-days-message-legend {
            display: flex;
            flex-direction: row;
            gap: 0.5rem;

            span {
                padding-top: 0.5rem;
            }
        }
    }

    .calendar-period-modal__footer {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding: 1rem;
        gap: 1rem;

        .calendar-period-modal__footer-right {
            display: flex;
            flex-direction: row;
            gap: 1rem;
        }
    }
}

/* Cercle avec dot pour semaine incomplète */

.date-warn-dot-tooltip {
    width: 16px;
    height: 8px;
    &::before {
        width: 16px !important;
        height: 16px !important;
    }
}

.date-warn-dot {
    position: relative;
    display: inline-block;
    &::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: 40px; /* Taille du cercle */
        height: 40px; /* Taille du cercle */
        border-radius: 50%; /* Cercle */
        border: 2px dashed var(--p-orange-200); /* Bordure en pointillés */
        transform: translate(-50%, -50%); /* Centre parfaitement le cercle */
    }
}

/* Bouton de raccourci */

.calendar-period__button {
    display: flex;
    align-items: center;
    color: #616161;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    transition: color 0.3s;
    gap: 8px;

    &:hover .icon {
        opacity: 1;
    }
    .icon {
        font-size: 14px;
        font-weight: 600;
        opacity: 0;
        transition: opacity 0.3s ease;
    }
}

.calendar-period__button--disabled {
    color: #dfe7ef;
    cursor: not-allowed;

    .icon {
        opacity: 0 !important;
    }
}
.calendar-period__button--focus {
    color: var(--p-cyan-400);

    .icon {
        opacity: 1;
    }
}
</style>
